"use client";

import React from "react";
import { Button } from "antd";

interface CalendarEvent {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
}

const formatDateTime = (iso: string) => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  }
  const pad = (value: number) => value.toString().padStart(2, "0");
  return [
    parsed.getUTCFullYear(),
    pad(parsed.getUTCMonth() + 1),
    pad(parsed.getUTCDate()),
    "T",
    pad(parsed.getUTCHours()),
    pad(parsed.getUTCMinutes()),
    pad(parsed.getUTCSeconds()),
    "Z",
  ].join("");
};

const escapeText = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\r?\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const createUid = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return `${crypto.randomUUID()}@openings.link`;
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}@openings.link`;
};

const buildIcsContent = (event: CalendarEvent) => {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Openings//Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${createUid()}`,
    `DTSTAMP:${formatDateTime(new Date().toISOString())}`,
    `DTSTART:${formatDateTime(event.startTime)}`,
    `DTEND:${formatDateTime(event.endTime)}`,
    `SUMMARY:${escapeText(event.title)}`,
  ];

  if (event.description) {
    lines.push(`DESCRIPTION:${escapeText(event.description)}`);
  }
  if (event.location) {
    lines.push(`LOCATION:${escapeText(event.location)}`);
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
};

const isIOSSafari = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone/.test(ua) && /WebKit/i.test(ua) && !/CriOS/i.test(ua);
};

const isIOSChrome = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  return /iPad|iPhone/.test(ua) && /CriOS/i.test(ua);
};

const formatForGoogle = (iso: string) => {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return iso.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  }
  return parsed
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}Z$/, "Z");
};

export function ICalendar({ event }: { event: CalendarEvent }) {
  const normalizedEvent = React.useMemo(
    () => ({
      ...event,
      endTime: event.endTime ?? event.startTime,
      description: event.description ?? "",
      location: event.location ?? "",
    }),
    [event],
  );

  const handleDownload = React.useCallback(() => {
    const icsContent = buildIcsContent(normalizedEvent);
    const dataUrl = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;

    if (isIOSChrome() || isIOSSafari()) {
      window.open(dataUrl, "_blank");
      return;
    }

    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "openings-appointment.ics";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }, [normalizedEvent]);

  const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    normalizedEvent.title,
  )}&details=${encodeURIComponent(
    normalizedEvent.description,
  )}&dates=${formatForGoogle(normalizedEvent.startTime)}/${formatForGoogle(
    normalizedEvent.endTime,
  )}&location=${encodeURIComponent(
    normalizedEvent.location,
  )}&sf=true&output=xml`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Button onClick={handleDownload}>
        Apple Calendar
      </Button>
      <div
        style={{
          width: 1,
          height: "1em",
          background:
            "var(--openings-booking-skin-divider, #d9d9d9)",
        }}
      />
      <Button
        href={googleCalendarUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        Google Calendar
      </Button>
    </div>
  );
}
